import {
  DeleteIcon,
  Divider,
  GKButton,
  GKSearchSelect,
  Grid2,
  IconButton,
  PlusIcon,
  PopConfirm,
  Show,
  Typography,
  UploadIcon,
} from "components";
import { orderBy } from "lodash";
import AuditFilesUpload from "Pages/AccountSetting/InvesterWebsite/AuditFilesUpload";
import { financialYearFilter } from "Pages/RevenueManagement/utils";
import { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useDeleteComplianceAuditReportMutation } from "store/features/MyWebsite";
import { useAppSelector } from "store/store";
import { handleReturnNaturalRowsForString } from "utils/formHelpers";
import { WebsiteDetails } from "./types";

const ComplianceAuditReportForm = () => {
  const form = useFormContext<WebsiteDetails>();

  const companyId = useAppSelector((state) => state?.user?.userData?.companyId);

  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState<
    boolean | null
  >(false);
  const [auditReportId, setAuditReportId] = useState<string | null>(null);
  const [showFilesModal, setShowFilesModal] = useState(false);
  const [attachmentsIndex, setAttachmentsIndex] = useState(0);

  const [deleteComplianceAuditReport, { isLoading }] =
    useDeleteComplianceAuditReportMutation();

  return (
    <>
      <Grid2 container spacing={3} p={2}>
        <Grid2 size={12}>
          <Typography fontSize={14} fontWeight={700} color="var(--neutral-900)">
            Compliance Audit Report
          </Typography>
        </Grid2>

        <Show.When
          isTrue={form.getValues("compliance.complianceAudit")?.length > 0}
        >
          <Grid2
            container
            size={12}
            spacing={2}
            display={"flex"}
            alignItems={"center"}
          >
            {form.watch("compliance.complianceAudit")?.map((item, index) => {
              return (
                <Grid2 size={12} container>
                  <Grid2
                    size={12}
                    display={"flex"}
                    alignItems={"center"}
                    gap={2}
                  >
                    <Grid2 size={4}>
                      <Controller
                        control={form.control}
                        name={`compliance.complianceAudit.${index}.year`}
                        rules={{
                          validate: (value) => {
                            const auditItem = form.getValues(
                              `compliance.complianceAudit.${index}`,
                            );
                            const hasReports =
                              (auditItem.report1 &&
                                auditItem.report1.length > 0) ||
                              (auditItem.report2 &&
                                auditItem.report2.length > 0);

                            if (hasReports && !value) {
                              return "Financial Year is required when reports are uploaded";
                            }

                            return true;
                          },
                        }}
                        render={({ field, fieldState: { error } }) => {
                          return (
                            <GKSearchSelect
                              inputLabel="Financial Year"
                              options={handleReturnNaturalRowsForString({
                                options: orderBy(
                                  financialYearFilter,
                                  "name",
                                  "desc",
                                ),
                                optionsValueKey: "name",
                                values: field.value || "",
                              })}
                              value={field.value}
                              onChange={(e, val) => {
                                field.onChange(val?.name);
                              }}
                              getOptionLabel={(option) => option.name || option}
                              error={!!error}
                              helperText={error?.message}
                            />
                          );
                        }}
                      />
                    </Grid2>
                    <Grid2 size={2.5}>
                      <GKButton
                        variant="outlined"
                        color="dark"
                        sx={{ height: "45px" }}
                        startIcon={<UploadIcon />}
                        onClick={() => {
                          setShowFilesModal(true);
                          setAttachmentsIndex(index);
                        }}
                      >
                        Upload Files
                      </GKButton>
                    </Grid2>
                    <Show.When
                      isTrue={
                        form.getValues("compliance.complianceAudit").length !==
                        1
                      }
                    >
                      <Grid2 size={1}>
                        <IconButton
                          color="error"
                          onClick={() => {
                            if (item?.id) {
                              setAuditReportId(item.id);
                              setOpenDeleteConfirmation(true);
                            } else {
                              const newObj = form
                                .getValues("compliance.complianceAudit")
                                .filter((d, i) => i !== index);

                              form.setValue(
                                "compliance.complianceAudit",
                                newObj,
                              );
                            }
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Grid2>
                    </Show.When>
                  </Grid2>
                  <Grid2 size={12}>
                    <Typography
                      fontSize={14}
                      fontWeight={400}
                      color={"var(--neutral-500)"}
                    >
                      Audit Report:{" "}
                      <span
                        style={{ fontWeight: 600, color: "var(--grey-900)" }}
                      >
                        {
                          form.watch(
                            `compliance.complianceAudit.${index}.report1`,
                          )?.length
                        }
                      </span>
                    </Typography>
                    <Typography
                      fontSize={14}
                      fontWeight={400}
                      color={"var(--neutral-500)"}
                    >
                      Adverse Findings:{" "}
                      <span
                        style={{ fontWeight: 500, color: "var(--grey-900)" }}
                      >
                        {
                          form.watch(
                            `compliance.complianceAudit.${index}.report2`,
                          )?.length
                        }
                      </span>
                    </Typography>
                  </Grid2>

                  <Grid2 size={12}>
                    <Divider />
                  </Grid2>
                </Grid2>
              );
            })}
          </Grid2>
        </Show.When>

        <Grid2 size={12}>
          <GKButton
            startIcon={<PlusIcon />}
            onClick={() => {
              const newObj = [
                ...form.getValues("compliance.complianceAudit"),
                {
                  report1: [],
                  report2: [],
                  year: "",
                },
              ];
              form.setValue("compliance.complianceAudit", newObj);
            }}
          >
            Add New
          </GKButton>
        </Grid2>

        <Show.When isTrue={showFilesModal}>
          <AuditFilesUpload
            setOpen={setShowFilesModal}
            open={showFilesModal}
            index={attachmentsIndex}
          />
        </Show.When>
      </Grid2>

      <Show.When isTrue={openDeleteConfirmation}>
        <PopConfirm
          anchorEl={null}
          open={openDeleteConfirmation}
          transformOrigin={{ vertical: "center", horizontal: "center" }}
          anchorOrigin={{ vertical: "center", horizontal: "center" }}
          setOpen={setOpenDeleteConfirmation}
          loading={isLoading}
          onSuccess={() => {
            try {
              deleteComplianceAuditReport({
                company: companyId,
                id: auditReportId,
              }).unwrap();
            } catch (err) {
              console.error("Delete compliance audit report failed:", err);
            }
          }}
        />
      </Show.When>
    </>
  );
};

export default ComplianceAuditReportForm;
